import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/tabs";
import { Switch } from "./components/ui/switch";
import {
  Users, Activity, Zap, Mail, Globe, FileText,
  Database, Key, ArrowLeft, Crown, Sun, Moon,
  BookOpen, PenLine, Megaphone, BarChart2, Share2, Plug, Mic, CreditCard, Inbox, TrendingUp, CalendarDays,
  Search as SearchIcon,
} from "lucide-react";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./components/ui/command";
import { BACKEND_URL } from "./lib/backend";
import { useDarkMode } from "./hooks/useDarkMode";
import UsersTab from "./components/admin/UsersTab";
import SystemTab from "./components/admin/SystemTab";
import AnalyticsTab from "./components/admin/AnalyticsTab";
import ActionsTab from "./components/admin/ActionsTab";
import NewslettersTab from "./components/admin/NewslettersTab";
import BriefTab from "./components/admin/BriefTab";
import TickerTab from "./components/admin/TickerTab";
import ApiKeysTab from "./components/admin/ApiKeysTab";
import SuggestionsTab from "./components/admin/SuggestionsTab";
import OfficinaTab from "./components/admin/OfficinaTab";
import ReportBriefsTab from "./components/admin/ReportBriefsTab";
import TravelEventsCard from "./components/admin/TravelEventsCard";
import MonetizeTab from "./components/admin/MonetizeTab";
import BillingTab from "./components/admin/BillingTab";
import EmailTab from "./components/admin/EmailTab";
import BeehiivPreviewModal from "./components/admin/BeehiivPreviewModal";
import SocialTab from "./components/admin/SocialTab";
import ExternalApiTab from "./components/admin/ExternalApiTab";
import DocCatalogTab from "./components/admin/DocCatalogTab";
import RankingHealthTab from "./components/admin/RankingHealthTab";
import PodcastTab from "./components/admin/PodcastTab";
import FullAutopilotTab from "./components/admin/FullAutopilotTab";
import SourcesTab from "./components/admin/SourcesTab";
import RejectedTab from "./components/admin/RejectedTab";
import { useBriefAdmin } from "./hooks/useBriefAdmin";
import { useNewslettersAdmin } from "./hooks/useNewslettersAdmin";

const API = `${BACKEND_URL}/api`;

/* ===== ADMIN NAV — 2-level menu: 5 groups → tabs (iter144 UX) ===== */
const ADMIN_GROUPS = [
  { id: "monitor",   label: "Monitor",   icon: Activity,   tabs: ["system", "analytics"] },
  { id: "content",   label: "Content",   icon: PenLine,    tabs: ["brief", "events", "newsletters", "podcast", "ticker", "suggestions", "officina", "report-briefs"] },
  { id: "pipeline",  label: "Pipeline",  icon: Zap,        tabs: ["sources", "rejected", "ranking", "actions", "full-autopilot"] },
  { id: "audience",  label: "Audience",  icon: Users,      tabs: ["users", "billing", "monetize", "email", "social"] },
  { id: "developer", label: "Developer", icon: Plug,       tabs: ["external-api", "apikeys", "doc-catalog"] },
];

const TAB_DEF = {
  "users":          { label: "Users",          icon: Users },
  "system":         { label: "System",         icon: Activity },
  "analytics":      { label: "Analytics",      icon: BarChart2 },
  "actions":        { label: "Actions",        icon: Zap },
  "newsletters":    { label: "Newsletter",     icon: BookOpen },
  "sources":        { label: "Sources",        icon: Activity },
  "rejected":       { label: "Scarti",         icon: Inbox, testid: "rejected-tab-trigger" },
  "suggestions":    { label: "Suggestions",    icon: Inbox, testid: "suggestions-tab-trigger" },
  "brief":          { label: "Brief",          icon: PenLine },
  "events":         { label: "Events",         icon: CalendarDays, testid: "events-tab-trigger" },
  "ticker":         { label: "Ticker",         icon: Megaphone },
  "apikeys":        { label: "Keys",           icon: Key },
  "monetize":       { label: "Monetize",       icon: Globe },
  "billing":        { label: "Billing",        icon: CreditCard, testid: "billing-tab-trigger" },
  "email":          { label: "Email",          icon: Mail },
  "social":         { label: "Social",         icon: Share2 },
  "external-api":   { label: "API + MCP",      icon: Plug },
  "doc-catalog":    { label: "Doc Catalog",    icon: FileText, testid: "doc-catalog-tab-trigger" },
  "ranking":        { label: "Ranking",        icon: TrendingUp, testid: "ranking-tab-trigger" },
  "podcast":        { label: "Podcast",        icon: Mic },
  "officina":       { label: "Officina",       icon: BookOpen, testid: "officina-tab-trigger" },
  "report-briefs":  { label: "Report Briefs",  icon: FileText, testid: "report-briefs-tab-trigger" },
  "full-autopilot": { label: "Full Autopilot", icon: Zap },
};

/* ===== ADMIN DASHBOARD ===== */
export default function AdminDashboard() {
  const { user, authHeader, logout } = useAuth();
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useDarkMode();
  const lastTabByGroup = useRef({});
  const [users, setUsers] = useState([]);
  // Users table pagination + sorting
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(20);
  const [usersPgSizeOpen, setUsersPgSizeOpen] = useState(false);
  const [usersSortDir, setUsersSortDir] = useState("desc");
  const [stats, setStats] = useState(null);
  const [triggerLoading, setTriggerLoading] = useState({});
  const [resendKey, setResendKey] = useState("");
  const [showResendKey, setShowResendKey] = useState(false);
  const [fromEmail, setFromEmail] = useState("noreply@yourdomain.com");
  const [fromName, setFromName] = useState("TourismIntel");
  const [siteUrl, setSiteUrl] = useState("https://your-site.com");
  const [saving, setSaving] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // ── Custom hooks (P3-3 refactor) ──────────────────────────────────────────
  const {
    brief, briefContent, setBriefContent, briefPhotoUrl, setBriefPhotoUrl,
    briefPublished, setBriefPublished, generatingBrief, savingBrief, loadingBrief,
    uploadingPhoto, socialTexts, setSocialTexts, briefOgPrompt, setBriefOgPrompt,
    regeneratingBriefOg, autoSettings, setAutoSettings, savingAutoSettings,
    editorComment, setEditorComment,
    editorUrls, setEditorUrls,
    newEditorUrl, setNewEditorUrl, editorNotesActive, savingEditorNotes, loadingEditorNotes,
    addingEditorUrl,
    photoInputRef,
    briefFeatured, loadingBriefFeatured,
    loadBriefFeatured, removeBriefFeatured, clearBriefFeatured,
    loadBrief, loadEditorNotes, saveEditorNotes, clearEditorNotes, addEditorUrl,
    saveAutoSettings, generateBrief, saveBrief, saveDraft, regenerateBriefOg, uploadBriefPhoto,
  } = useBriefAdmin(authHeader);

  const {
    newsletters, loadingNL, expandedNL, setExpandedNL, loadNewsletters,
    nlFeatured, loadingNlFeatured, loadNlFeatured, removeNlFeatured, clearNlFeatured,
  } = useNewslettersAdmin(authHeader);

  // API keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [keyValues, setKeyValues] = useState({});
  const [showKey, setShowKey] = useState({});
  const [savingKeys, setSavingKeys] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [testResults, setTestResults] = useState({});

  // Summary model state
  const [summaryModel, setSummaryModel] = useState("gpt-4o-mini");
  const [availableModels, setAvailableModels] = useState([]);
  const [savingModel, setSavingModel] = useState(false);

  // Add user state
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserIsAdmin, setNewUserIsAdmin] = useState(false);
  const [newUserIsPro, setNewUserIsPro] = useState(false);
  const [newUserSendLink, setNewUserSendLink] = useState(true);
  const [addingUser, setAddingUser] = useState(false);

  // Brevo stats state
  const [brevoStats, setBrevoStats] = useState(null);
  const [loadingBrevo, setLoadingBrevo] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState({});
  const [publishingBeehiiv, setPublishingBeehiiv] = useState({});

  // Beehiiv preview modal
  const [beehiivPreview, setBeehiivPreview] = useState(null);

  // Ticker state
  const [tickerItems, setTickerItems] = useState([]);
  const [tickerEnabled, setTickerEnabled] = useState(false);
  const [tickerPause, setTickerPause] = useState(3);
  const [tickerAutopilot, setTickerAutopilot] = useState(false);
  const [tickerAiCurated, setTickerAiCurated] = useState(false);
  const [tickerLastAutopilotAt, setTickerLastAutopilotAt] = useState(null);
  const [autopilotHistory, setAutopilotHistory] = useState([]);
  const [runningAutopilot, setRunningAutopilot] = useState(false);

  // Sources & Discovery state
  const [sourcesHealth, setSourcesHealth] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [ppEnabled, setPpEnabled] = useState(true);
  const [ppTopics, setPpTopics] = useState([]);
  const [ppLastRunAt, setPpLastRunAt] = useState(null);
  const [ppLastRunCount, setPpLastRunCount] = useState(0);
  const [savingPp, setSavingPp] = useState(false);

  // Topic Watchers state
  const [topicWatchers, setTopicWatchers] = useState([]);
  const [savingWatcher, setSavingWatcher] = useState({});

  // Custom sources state
  const [customSources, setCustomSources] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => (TAB_DEF[searchParams.get("tab")] ? searchParams.get("tab") : "users"));
  const [cmdOpen, setCmdOpen] = useState(false);
  const activeGroup = ADMIN_GROUPS.find(g => g.tabs.includes(activeTab)) || ADMIN_GROUPS[0];
  const selectTab = (tabId) => {
    const g = ADMIN_GROUPS.find(x => x.tabs.includes(tabId));
    if (g) lastTabByGroup.current[g.id] = tabId;
    setActiveTab(tabId);
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set("tab", tabId);
      return p;
    }, { replace: true });
  };
  const openGroup = (g) => {
    const target = lastTabByGroup.current[g.id] || g.tabs[0];
    tabLoaders[target]?.();
    selectTab(target);
  };
  const tabLoaders = {
    "analytics":   () => loadAnalytics(),
    "actions":     () => loadSummaryModel(),
    "newsletters": () => { loadNewsletters(); loadNlFeatured(); },
    "sources":     () => { loadSources(); loadTopicWatchers(); loadCustomSources(); loadSemanticDedup(); },
    "brief":       () => { loadBrief(); loadEditorNotes(); loadBriefFeatured(); },
    "ticker":      () => loadTicker(),
    "apikeys":     () => loadApiKeys(),
    "monetize":    () => loadAdsenseConfig(),
  };

  useEffect(() => {
    // deep-linked tab (?tab=) — fire its loader once on mount
    tabLoaders[activeTab]?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // "Add as source" handoff: Suggestions tab → Sources tab quick-add prefill (iter86b)
  const [sourcePrefillUrl, setSourcePrefillUrl] = useState("");
  // Semantic dedup state
  const [semanticDedup, setSemanticDedup] = useState({ enabled: false, last_run_at: null, last_removed: 0 });
  const [savingSemanticDedup, setSavingSemanticDedup] = useState(false);
  const [savingTicker, setSavingTicker] = useState(false);
  const [loadingTicker, setLoadingTicker] = useState(false);
  const [newTickerText, setNewTickerText] = useState("");
  const [newTickerUrl, setNewTickerUrl] = useState("");
  const [adsPublisherId, setAdsPublisherId] = useState("");
  const [adsSlotId, setAdsSlotId] = useState("");
  const [savingAds, setSavingAds] = useState(false);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [seedingTopPerformers, setSeedingTopPerformers] = useState(false);

  // AdSense state
  const [adsEnabled, setAdsEnabled] = useState(false);

  // Badge count for the Suggestions tab (new user source suggestions)
  const [newSuggestionsCount, setNewSuggestionsCount] = useState(0);

  useEffect(() => {
    loadData();
    axios.get(`${API}/admin/source-suggestions`, { headers: authHeader() })
      .then(r => setNewSuggestionsCount(r.data.counts?.new || 0))
      .catch(() => {});
    // Handle Spreaker OAuth2 callback params
    const params = new URLSearchParams(window.location.search);
    if (params.get("spreaker_connected") === "1") {
      toast.success("Spreaker connected successfully!");
      window.history.replaceState({}, "", "/admin");
    } else if (params.get("spreaker_error")) {
      const err = params.get("spreaker_error");
      const debug = params.get("spreaker_debug");
      const msg = debug ? `Spreaker error: ${err} — ${debug}` : `Spreaker connection failed: ${err}`;
      toast.error(msg, { duration: 15000 });
      window.history.replaceState({}, "", "/admin");
    }
  }, []);

  const loadData = async (sortDir = "desc") => {
    try {
      setLoadingUsers(true);
      const [usersRes, statsRes, emailRes] = await Promise.all([
        axios.get(`${API}/admin/users?sort_dir=${sortDir}`, { headers: authHeader() }),
        axios.get(`${API}/admin/stats`, { headers: authHeader() }),
        axios.get(`${API}/admin/email-config`, { headers: authHeader() }),
      ]);
      setUsers(usersRes.data.users || []);
      setUsersPage(1);
      setStats(statsRes.data);
      const cfg = emailRes.data;
      if (cfg.resend_api_key) setResendKey(cfg.resend_api_key);
      setFromEmail(cfg.from_email || "noreply@yourdomain.com");
      setFromName(cfg.from_name || "TourismIntel");
      setSiteUrl(cfg.site_url || "https://your-site.com");
    } catch (e) { console.error(e); }
    finally { setLoadingUsers(false); }
  };

  const loadApiKeys = useCallback(async () => {
    setLoadingKeys(true);
    try {
      const res = await axios.get(`${API}/admin/api-keys`, { headers: authHeader() });
      setApiKeys(res.data.keys || []);
      const init = {};
      (res.data.keys || []).forEach(k => { init[k.name] = ""; });
      setKeyValues(init);
    } catch (e) { toast.error("Failed to load API keys"); }
    finally { setLoadingKeys(false); }
  }, [authHeader]);

  const loadAdsenseConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/adsense-config`);
      setAdsEnabled(res.data.enabled || false);
      setAdsPublisherId(res.data.publisher_id || "");
      setAdsSlotId(res.data.slot_id || "");
    } catch (e) { console.error(e); }
  }, []);

  const loadBrevoStats = useCallback(async () => {
    setLoadingBrevo(true);
    try {
      const res = await axios.get(`${API}/admin/brevo/stats`, { headers: authHeader() });
      setBrevoStats(res.data);
    } catch (e) { toast.error("Failed to load Brevo stats"); }
    finally { setLoadingBrevo(false); }
  }, [authHeader]);

  const syncAllToBrevo = async () => {
    setSyncingAll(true);
    try {
      const res = await axios.post(`${API}/admin/brevo/sync-all`, {}, { headers: authHeader() });
      toast.success(`Synced ${res.data.synced}/${res.data.total} users to Brevo${res.data.failed > 0 ? ` (${res.data.failed} failed)` : ""}`);
      await loadBrevoStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncingAll(false);
    }
  };

  const loadAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API}/admin/analytics`, { headers: authHeader() });
      setAnalytics(res.data);
    } catch (e) { toast.error("Failed to load analytics"); }
    finally { setLoadingAnalytics(false); }
  }, [authHeader]);

  const loadTicker = useCallback(async () => {
    setLoadingTicker(true);
    try {
      const res = await axios.get(`${API}/admin/ticker`, { headers: authHeader() });
      setTickerItems(res.data.items || []);
      setTickerEnabled(res.data.is_enabled ?? true);
      setTickerPause(res.data.pause_seconds ?? 3);
      setTickerAutopilot(res.data.autopilot_enabled ?? false);
      setTickerAiCurated(res.data.ai_curated ?? false);
      setTickerLastAutopilotAt(res.data.last_autopilot_at || null);
      try {
        const h = await axios.get(`${API}/admin/ticker/autopilot/history`, { headers: authHeader() });
        setAutopilotHistory(h.data.history || []);
      } catch (_e) { /* non-fatal */ }
    } catch (e) { toast.error("Failed to load ticker"); }
    finally { setLoadingTicker(false); }
  }, [authHeader]);

  const saveTicker = async () => {
    setSavingTicker(true);
    try {
      await axios.put(`${API}/admin/ticker`, {
        items: tickerItems,
        pause_seconds: tickerPause,
        is_enabled: tickerEnabled,
        autopilot_enabled: tickerAutopilot,
        ai_curated: tickerAiCurated,
      }, { headers: authHeader() });
      toast.success("Ticker saved!");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingTicker(false); }
  };

  const runAutopilotNow = async () => {
    setRunningAutopilot(true);
    try {
      const res = await axios.post(`${API}/admin/ticker/autopilot/run`, {}, { headers: authHeader() });
      setTickerItems(res.data.items || []);
      setTickerLastAutopilotAt(res.data.ran_at || null);
      toast.success(`Autopilot picked ${res.data.count} top stories`);
      // Refresh history
      try {
        const h = await axios.get(`${API}/admin/ticker/autopilot/history`, { headers: authHeader() });
        setAutopilotHistory(h.data.history || []);
      } catch (_e) { /* non-fatal */ }
    } catch (e) { toast.error(e.response?.data?.detail || "Autopilot failed"); }
    finally { setRunningAutopilot(false); }
  };

  const restoreFromHistory = async (run) => {
    if (!run?.items?.length) return;
    try {
      await axios.put(`${API}/admin/ticker`, {
        items: run.items,
        pause_seconds: tickerPause,
        is_enabled: tickerEnabled,
        autopilot_enabled: tickerAutopilot,
        ai_curated: tickerAiCurated,
      }, { headers: authHeader() });
      setTickerItems(run.items);
      toast.success(`Restored ${run.items.length} items from ${run.mode === "ai-curated" ? "AI" : "auto"} run`);
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };

  const loadSources = useCallback(async () => {
    setLoadingSources(true);
    try {
      const [hRes, pRes] = await Promise.all([
        axios.get(`${API}/admin/sources/health`, { headers: authHeader() }),
        axios.get(`${API}/admin/perplexity-discovery`, { headers: authHeader() }),
      ]);
      setSourcesHealth(hRes.data.sources || []);
      setPpEnabled(pRes.data.enabled ?? true);
      setPpTopics(pRes.data.topics || []);
      setPpLastRunAt(pRes.data.last_run_at || null);
      setPpLastRunCount(pRes.data.last_run_count || 0);
    } catch (e) { toast.error("Failed to load sources"); }
    finally { setLoadingSources(false); }
  }, [authHeader]);

  const savePpDiscovery = async () => {
    setSavingPp(true);
    try {
      await axios.put(`${API}/admin/perplexity-discovery`, {
        enabled: ppEnabled, topics: ppTopics,
      }, { headers: authHeader() });
      toast.success("Discovery settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingPp(false); }
  };

  const loadTopicWatchers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/topic-watchers`, { headers: authHeader() });
      setTopicWatchers(r.data.watchers || []);
    } catch (_e) { /* non-fatal */ }
  }, [authHeader]);

  const updateWatcher = async (id, patch) => {
    setSavingWatcher(s => ({ ...s, [id]: true }));
    try {
      await axios.put(`${API}/admin/topic-watchers/${id}`, patch, { headers: authHeader() });
      setTopicWatchers(prev => prev.map(w => w.id === id ? { ...w, ...patch } : w));
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingWatcher(s => ({ ...s, [id]: false })); }
  };

  const runWatcherNow = async (id) => {
    setSavingWatcher(s => ({ ...s, [id]: true }));
    try {
      const r = await axios.post(`${API}/admin/topic-watchers/${id}/run`, {}, { headers: authHeader() });
      toast.success(`Watcher ran — ${r.data.saved} new articles`);
      await loadTopicWatchers();
    } catch (e) { toast.error(e.response?.data?.detail || "Watcher failed"); }
    finally { setSavingWatcher(s => ({ ...s, [id]: false })); }
  };

  const deleteWatcher = async (id) => {
    if (!window.confirm("Delete this watcher?")) return;
    try {
      await axios.delete(`${API}/admin/topic-watchers/${id}`, { headers: authHeader() });
      setTopicWatchers(prev => prev.filter(w => w.id !== id));
      toast.success("Watcher deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  const createWatcher = async (data) => {
    try {
      const r = await axios.post(`${API}/admin/topic-watchers`, data, { headers: authHeader() });
      setTopicWatchers(prev => [...prev, r.data.watcher]);
      toast.success("Watcher created");
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); return false; }
  };

  const loadCustomSources = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/custom-sources`, { headers: authHeader() });
      setCustomSources(r.data.sources || []);
    } catch (_e) { /* non-fatal */ }
  }, [authHeader]);

  const verifyCustomSource = async (rss_url) => {
    try {
      const r = await axios.post(`${API}/admin/custom-sources/verify`, { rss_url }, { headers: authHeader() });
      return r.data;
    } catch (e) {
      return { ok: false, error: e.response?.data?.detail || "Verify failed" };
    }
  };

  const verifyCustomSourceHtml = async (index_url, include_pattern) => {
    try {
      const r = await axios.post(
        `${API}/admin/custom-sources/verify-html`,
        { index_url, include_pattern },
        { headers: authHeader() }
      );
      return r.data;
    } catch (e) {
      return { ok: false, error: e.response?.data?.detail || "Map failed" };
    }
  };

  const createCustomSource = async (data) => {
    try {
      const r = await axios.post(`${API}/admin/custom-sources`, data, { headers: authHeader() });
      setCustomSources(prev => [r.data.source, ...prev]);
      toast.success(`Source "${data.name}" added`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
      return false;
    }
  };

  const deleteCustomSource = async (name) => {
    if (!window.confirm(`Delete custom source "${name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/custom-sources/${encodeURIComponent(name)}`, { headers: authHeader() });
      setCustomSources(prev => prev.filter(c => c.name !== name));
      toast.success("Source deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  const updateCustomSource = async (name, patch) => {
    try {
      const r = await axios.patch(
        `${API}/admin/custom-sources/${encodeURIComponent(name)}`,
        patch,
        { headers: authHeader() }
      );
      setCustomSources(prev => prev.map(c => (c.name === name ? r.data.source : c)));
      toast.success("Source updated");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
      return false;
    }
  };

  // Semantic dedup config
  const loadSemanticDedup = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/semantic-dedup`, { headers: authHeader() });
      setSemanticDedup(r.data);
    } catch (_e) { /* non-fatal */ }
  }, [authHeader]);

  const saveSemanticDedup = async (enabled) => {
    setSavingSemanticDedup(true);
    try {
      const r = await axios.put(
        `${API}/admin/semantic-dedup`,
        { enabled },
        { headers: authHeader() }
      );
      setSemanticDedup(prev => ({ ...prev, enabled: r.data.enabled }));
      toast.success(`Semantic dedup ${r.data.enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSavingSemanticDedup(false);
    }
  };

  // Seed featured_for_brief with top performers — applies to brief, auto-newsletter, podcast
  const seedTopPerformersForBrief = async (count = 3) => {
    setSeedingTopPerformers(true);
    try {
      const r = await axios.post(
        `${API}/admin/brief/use-top-performers`,
        { count },
        { headers: authHeader() }
      );
      const titles = (r.data?.titles || []).slice(0, 2).join(" · ");
      toast.success(
        `Seeded ${r.data.featured_count} top performer${r.data.featured_count === 1 ? "" : "s"} for next brief${titles ? `: ${titles}…` : ""}`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed failed");
    } finally {
      setSeedingTopPerformers(false);
    }
  };


  const addTickerItem = () => {
    if (!newTickerText.trim()) return;
    setTickerItems(prev => [...prev, { text: newTickerText.trim(), url: newTickerUrl.trim() }]);
    setNewTickerText("");
    setNewTickerUrl("");
  };

  const removeTickerItem = (i) => setTickerItems(prev => prev.filter((_, idx) => idx !== i));

  const sendBrevoNewsletter = async (nl, subjectOverride = "") => {
    setSendingCampaign(p => ({ ...p, [nl.id]: true }));
    try {
      const body = subjectOverride.trim() ? { subject_override: subjectOverride.trim() } : {};
      const res = await axios.post(`${API}/admin/newsletters/${nl.id}/send-campaign`, body, { headers: authHeader() });
      toast.success(`Campaign sent! ID: ${res.data.campaign_id}`);
      loadNewsletters();
    } catch (e) { toast.error(e.response?.data?.detail || "Campaign failed"); }
    finally { setSendingCampaign(p => ({ ...p, [nl.id]: false })); }
  };

  const publishToBeehiiv = async (nl) => {
    setPublishingBeehiiv(p => ({ ...p, [nl.id]: true }));
    try {
      const res = await axios.post(`${API}/admin/newsletters/${nl.id}/publish-beehiiv`, {}, { headers: authHeader() });
      toast.success(`Published to Beehiiv! Post ID: ${res.data.post_id}`);
      setBeehiivPreview(null);
      loadNewsletters();
    } catch (e) { toast.error(e.response?.data?.detail || "Beehiiv publish failed"); }
    finally { setPublishingBeehiiv(p => ({ ...p, [nl.id]: false })); }
  };

  const dm = darkMode;

  const handleTrigger = async (action) => {
    setTriggerLoading(p => ({ ...p, [action]: true }));
    const endpoints = {
      refresh: "/news/refresh",
      perplexity: "/news/perplexity-search",
      discovery: "/news/massive-discovery",
      historical: "/archive/massive-historical-discovery",
    };
    try {
      const res = await axios.post(`${API}${endpoints[action]}`, {}, { headers: authHeader() });
      const d = res.data;
      // Show a result-aware toast
      if (action === "discovery") {
        if (d.articles_saved > 0) toast.success(`Massive Discovery: ${d.articles_saved} new articles saved (${d.articles_skipped} duplicates skipped)`);
        else toast.info(`Massive Discovery ran but found 0 new articles. Check your Perplexity API quota.`);
      } else if (action === "historical") {
        toast.info(d.message || "Historical discovery started in background — check Activity Log for results.");
      } else if (action === "perplexity") {
        const saved = d.articles_saved ?? d.articles_added ?? 0;
        toast.success(saved > 0 ? `Perplexity search: ${saved} new articles added` : "Perplexity search ran — 0 new articles found. Check quota.");
      } else {
        const saved = d.articles_saved ?? d.new_articles ?? 0;
        toast.success(saved > 0 ? `Feed refresh: ${saved} new articles added` : "Feed refreshed — no new articles found.");
      }
      if (action !== "historical") loadData();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (detail.toLowerCase().includes("quota") || detail.toLowerCase().includes("credits")) {
        toast.error("Perplexity quota exceeded — add credits at perplexity.ai/settings/api", { duration: 8000 });
      } else {
        toast.error(`Failed: ${detail || e.message}`, { duration: 6000 });
      }
    } finally {
      setTriggerLoading(p => ({ ...p, [action]: false }));
    }
  };

  const toggleAdmin = async (userId, current) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}`, { is_admin: !current }, { headers: authHeader() });
      setUsers(us => us.map(u => u.id === userId ? res.data : u));
      toast.success(!current ? "Admin role granted" : "Admin role removed");
    } catch (e) { toast.error("Failed to update user"); }
  };

  const togglePro = async (userId, current) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}`, { is_pro: !current }, { headers: authHeader() });
      setUsers(us => us.map(u => u.id === userId ? res.data : u));
      toast.success(!current ? "PRO access granted (web + mobile)" : "PRO access revoked");
    } catch (e) { toast.error("Failed to update user"); }
  };

  const toggleActive = async (userId, current) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}`, { is_active: !current }, { headers: authHeader() });
      setUsers(us => us.map(u => u.id === userId ? res.data : u));
      toast.success(!current ? "User activated" : "User suspended");
    } catch (e) { toast.error("Failed to update user"); }
  };

  const confirmUser = async (userId) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}`, { is_confirmed: true }, { headers: authHeader() });
      setUsers(us => us.map(u => u.id === userId ? res.data : u));
      toast.success("User confirmed");
    } catch (e) { toast.error("Failed"); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Delete this user permanently?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: authHeader() });
      setUsers(us => us.filter(u => u.id !== userId));
      toast.success("User deleted");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "unknown error";
      toast.error(`Failed to delete user (${status || "?"}): ${detail}`);
      console.error("deleteUser failed:", e);
    }
  };

  const saveEmailConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/email-config`, { resend_api_key: resendKey, from_email: fromEmail, from_name: fromName, site_url: siteUrl }, { headers: authHeader() });
      toast.success("Email configuration saved");
    } catch (e) { toast.error("Failed to save config"); }
    finally { setSaving(false); }
  };

  const testEmail = async () => {
    try {
      const res = await axios.post(`${API}/admin/email-config/test`, {}, { headers: authHeader() });
      toast.success(res.data.message);
    } catch (e) { toast.error("Test failed", { description: e.response?.data?.detail }); }
  };

  // Newsletter actions
  const togglePublish = async (nl) => {
    try {
      await axios.put(`${API}/admin/newsletters/${nl.id}`, { is_published: !nl.is_published }, { headers: authHeader() });
      toast.success(nl.is_published ? "Newsletter unpublished" : "Newsletter published");
      loadNewsletters();
    } catch (e) { toast.error("Failed to update newsletter"); }
  };

  const deleteNewsletter = async (id) => {
    if (!window.confirm("Delete this newsletter permanently?")) return;
    try {
      await axios.delete(`${API}/admin/newsletters/${id}`, { headers: authHeader() });
      loadNewsletters();
      toast.success("Newsletter deleted");
    } catch (e) { toast.error("Failed to delete newsletter"); }
  };

  // API Keys actions
  const saveApiKeys = async () => {
    setSavingKeys(true);
    try {
      const toSave = {};
      Object.entries(keyValues).forEach(([k, v]) => { if (v && v.trim()) toSave[k] = v.trim(); });
      if (Object.keys(toSave).length === 0) { toast.info("No new keys to save"); setSavingKeys(false); return; }
      const res = await axios.put(`${API}/admin/api-keys`, { keys: toSave }, { headers: authHeader() });
      toast.success(`Saved: ${res.data.saved_keys.join(", ")}`);
      setKeyValues(prev => { const n = { ...prev }; res.data.saved_keys.forEach(k => { n[k] = ""; }); return n; });
      loadApiKeys();
    } catch (e) { toast.error("Failed to save keys"); }
    finally { setSavingKeys(false); }
  };

  const saveAdsenseConfig = async () => {
    setSavingAds(true);
    try {
      await axios.put(`${API}/admin/adsense-config`, { enabled: adsEnabled, publisher_id: adsPublisherId, slot_id: adsSlotId }, { headers: authHeader() });
      toast.success("AdSense configuration saved");
    } catch (e) { toast.error("Failed to save AdSense config"); }
    finally { setSavingAds(false); }
  };

  const removeApiKey = async (keyName) => {
    if (!window.confirm(`Remove stored ${keyName} from database? The environment variable fallback will be used.`)) return;
    try {
      await axios.delete(`${API}/admin/api-keys/${keyName}`, { headers: authHeader() });
      toast.success(`${keyName} removed from database`);
      loadApiKeys();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const testApiKey = async (keyName) => {
    const keyValue = keyValues[keyName]?.trim() || null;
    setTestResults(prev => ({ ...prev, [keyName]: { status: "testing", message: "" } }));
    try {
      const res = await axios.post(
        `${API}/admin/api-keys/test`,
        { key_name: keyName, key_value: keyValue || undefined },
        { headers: authHeader() }
      );
      setTestResults(prev => ({
        ...prev,
        [keyName]: { status: res.data.success ? "success" : "error", message: res.data.message }
      }));
    } catch (e) {
      setTestResults(prev => ({
        ...prev,
        [keyName]: { status: "error", message: e.response?.data?.detail || "Connection test failed" }
      }));
    }
  };

  const loadSummaryModel = async () => {
    try {
      const res = await axios.get(`${API}/admin/summary-model`, { headers: authHeader() });
      setSummaryModel(res.data.model_id);
      setAvailableModels(res.data.available_models || []);
    } catch (e) { /* silent */ }
  };

  const saveSummaryModel = async (modelId) => {
    setSavingModel(true);
    try {
      await axios.put(`${API}/admin/summary-model`, { model_id: modelId }, { headers: authHeader() });
      setSummaryModel(modelId);
      toast.success("Summary model updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save model"); }
    finally { setSavingModel(false); }
  };

  const addUser = async (e) => {
    e.preventDefault();
    if (!newUserEmail.trim()) return;
    setAddingUser(true);
    try {
      const res = await axios.post(`${API}/admin/users`,
        { email: newUserEmail.trim().toLowerCase(), is_admin: newUserIsAdmin, is_pro: newUserIsPro, send_magic_link: newUserSendLink },
        { headers: authHeader() }
      );
      toast.success(
        res.data.email_sent
          ? `${newUserEmail} added — magic link sent`
          : res.data.dev_token
          ? `${newUserEmail} added (dev token: ${res.data.dev_token.slice(0, 8)}…)`
          : `${newUserEmail} added successfully`
      );
      setNewUserEmail(""); setNewUserIsAdmin(false); setNewUserIsPro(false); setNewUserSendLink(true);
      setShowAddUser(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add user"); }
    finally { setAddingUser(false); }
  };

  const statCards = [
    { icon: FileText, label: "Total Articles", value: stats?.total_articles ?? "…", sub: stats ? `+${stats.articles_today} today` : "loading", color: "text-[#006874]" },
    { icon: Users, label: "Registered Users", value: stats?.total_users ?? "…", sub: stats ? `${stats.confirmed_users} confirmed` : "loading", color: "text-emerald-600" },
    { icon: Database, label: "Archive", value: stats ? `${stats.archive_months} mo` : "…", sub: `${stats?.sources_active ?? 14} active sources`, color: "text-violet-600" },
    { icon: Zap, label: "AI Calls Today", value: stats?.articles_today ?? "…", sub: "OpenAI summaries", color: "text-amber-600" },
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${dm ? "bg-[#0c1011]" : "bg-[#f8f9fa]"}`}>

      {/* HEADER */}
      <header className={`sticky top-0 z-50 glass-nav`}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 sm:gap-6 min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <img
                src={dm ? "/TI_logo_white_crop.png" : "/TI_logo_color_crop.png"}
                alt="tourismintel.ai"
                style={{ height: 24, width: 110, objectFit: "contain", maxWidth: "none" }}
                className="sm:h-7 sm:w-32"
              />
              <span className={`category-tag px-2 py-0.5 rounded-md ${dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/8 text-[#006874]"}`}>Admin</span>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/feed" data-testid="back-to-feed-desktop" className={`category-tag transition-colors flex items-center gap-1 ${dm ? "text-slate-500 hover:text-slate-300" : "text-[#6b7a7d] hover:text-[#191c1d]"}`}><ArrowLeft className="w-3 h-3" /> Back to Feed</Link>
            </nav>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <Link to="/feed" data-testid="back-to-feed-mobile" className={`md:hidden p-1.5 rounded-lg transition-colors ${dm ? "text-slate-400 hover:bg-[#1e2627]" : "text-[#6b7a7d] hover:bg-[#f3f4f5]"}`} title="Back to Feed">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <span className={`hidden sm:inline category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"} max-w-[120px] sm:max-w-none truncate`}>{user?.email}</span>
            <div className={`flex items-center gap-1.5 pl-2 sm:pl-3 ml-0 sm:ml-1 border-l ${dm ? "border-white/8" : "border-black/8"}`}>
              <Sun className={`w-3.5 h-3.5 ${dm ? "text-slate-600" : "text-[#006874]"}`} />
              <Switch data-testid="admin-dark-mode-switch" checked={darkMode} onCheckedChange={setDarkMode} className="data-[state=checked]:bg-[#006874] scale-90" />
              <Moon className={`w-3.5 h-3.5 ${dm ? "text-cyan-400" : "text-[#bac9cc]"}`} />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-20">
        <div className="mb-6 sm:mb-10">
          <p className={`category-tag mb-1 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>Control Center</p>
          <h1 className={`editorial-heading text-2xl sm:text-4xl font-medium tracking-tight ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Admin Dashboard</h1>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-10">
          {statCards.map(({ icon: Icon, label, value, sub, color }) => (
            <div key={label} className={`rounded-2xl p-3 sm:p-5 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
              <Icon className={`w-4 h-4 mb-2 sm:mb-3 ${color}`} />
              <p className={`editorial-heading text-2xl sm:text-3xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>{value}</p>
              <p className={`category-tag mt-0.5 text-xs sm:text-xs ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>{label}</p>
              <p className={`text-xs mt-1 hidden sm:block ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{sub}</p>
            </div>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={selectTab} className="w-full">
          <CommandDialog open={cmdOpen} onOpenChange={setCmdOpen}>
            <CommandInput placeholder="Jump to section…" data-testid="admin-cmdk-input" />
            <CommandList>
              <CommandEmpty>No section found.</CommandEmpty>
              {ADMIN_GROUPS.map((g) => (
                <CommandGroup key={g.id} heading={g.label}>
                  {g.tabs.map((t) => {
                    const def = TAB_DEF[t];
                    const TIcon = def.icon;
                    return (
                      <CommandItem
                        key={t}
                        value={`${def.label} ${g.label}`}
                        data-testid={`cmdk-item-${t}`}
                        onSelect={() => { tabLoaders[t]?.(); selectTab(t); setCmdOpen(false); }}
                      >
                        <TIcon className="w-3.5 h-3.5 mr-2" />{def.label}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
            </CommandList>
          </CommandDialog>
          <div className="mb-6 sm:mb-8 space-y-2">
            {/* Level 1 — groups */}
            <div
              className={`flex gap-1 p-1 rounded-xl overflow-x-auto ${dm ? "bg-[#1a2122]" : "bg-[#f3f4f5]"}`}
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {ADMIN_GROUPS.map((g) => {
                const isActive = g.tabs.includes(activeTab);
                const GIcon = g.icon;
                return (
                  <button
                    key={g.id}
                    onClick={() => openGroup(g)}
                    data-testid={`admin-group-${g.id}`}
                    className={`flex-1 min-w-fit category-tag rounded-lg px-3 py-2 flex items-center justify-center gap-1.5 whitespace-nowrap transition-colors ${
                      isActive
                        ? (dm ? "bg-[#0c1011] text-cyan-300" : "bg-white text-[#006874] shadow-sm")
                        : (dm ? "text-slate-400 hover:text-slate-200" : "text-[#6b7a7d] hover:text-[#191c1d]")
                    }`}
                  >
                    <GIcon className="w-3 h-3" />{g.label}
                    {g.id === "content" && newSuggestionsCount > 0 && (
                      <span data-testid="admin-group-content-badge" className={`min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}>{newSuggestionsCount}</span>
                    )}
                  </button>
                );
              })}
              <button
                onClick={() => setCmdOpen(true)}
                data-testid="admin-cmdk-button"
                title="Quick jump (Cmd+K / Ctrl+K)"
                className={`flex-shrink-0 category-tag rounded-lg px-2.5 py-2 flex items-center gap-1 transition-colors ${dm ? "text-slate-500 hover:text-slate-300" : "text-[#9aa8ab] hover:text-[#191c1d]"}`}
              >
                <SearchIcon className="w-3 h-3" />
                <span className="hidden sm:inline text-[10px] font-mono">⌘K</span>
              </button>
            </div>
            {/* Level 2 — tabs of the active group */}
            <div className="overflow-x-auto" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
              <TabsList className={`flex gap-1 w-max min-w-full p-1 rounded-xl bg-transparent ${dm ? "bg-[#1a2122]/60" : "bg-[#f3f4f5]/70"}`}>
                {activeGroup.tabs.map((t) => {
                  const def = TAB_DEF[t];
                  const TIcon = def.icon;
                  return (
                    <TabsTrigger
                      key={t}
                      value={t}
                      onClick={() => tabLoaders[t]?.()}
                      {...(def.testid ? { "data-testid": def.testid } : {})}
                      className="flex-shrink-0 category-tag rounded-lg flex items-center gap-1.5"
                    >
                      <TIcon className="w-3 h-3" />{def.label}
                      {t === "suggestions" && newSuggestionsCount > 0 && (
                        <span data-testid="suggestions-tab-badge" className={`min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}>{newSuggestionsCount}</span>
                      )}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>
          </div>

          <UsersTab
            dm={dm} users={users} usersPage={usersPage} setUsersPage={setUsersPage}
            usersPageSize={usersPageSize} setUsersPageSize={setUsersPageSize}
            usersPgSizeOpen={usersPgSizeOpen} setUsersPgSizeOpen={setUsersPgSizeOpen}
            usersSortDir={usersSortDir} setUsersSortDir={setUsersSortDir}
            loadData={loadData} showAddUser={showAddUser} setShowAddUser={setShowAddUser}
            newUserEmail={newUserEmail} setNewUserEmail={setNewUserEmail}
            newUserIsAdmin={newUserIsAdmin} setNewUserIsAdmin={setNewUserIsAdmin}
            newUserIsPro={newUserIsPro} setNewUserIsPro={setNewUserIsPro}
            newUserSendLink={newUserSendLink} setNewUserSendLink={setNewUserSendLink}
            addingUser={addingUser} addUser={addUser}
            toggleAdmin={toggleAdmin} togglePro={togglePro} toggleActive={toggleActive}
            confirmUser={confirmUser} deleteUser={deleteUser}
            authHeader={authHeader}
          />

          <SystemTab dm={dm} stats={stats} />

          <AnalyticsTab
            dm={dm}
            analytics={analytics}
            loadingAnalytics={loadingAnalytics}
            seedTopPerformersForBrief={seedTopPerformersForBrief}
            seedingTopPerformers={seedingTopPerformers}
          />

          <ActionsTab
            dm={dm} triggerLoading={triggerLoading} handleTrigger={handleTrigger}
            summaryModel={summaryModel} availableModels={availableModels}
            savingModel={savingModel} saveSummaryModel={saveSummaryModel}
            authHeader={authHeader}
          />

          <NewslettersTab
            dm={dm} newsletters={newsletters} loadingNL={loadingNL}
            loadNewsletters={loadNewsletters} expandedNL={expandedNL} setExpandedNL={setExpandedNL}
            togglePublish={togglePublish} deleteNewsletter={deleteNewsletter}
            sendBrevoNewsletter={sendBrevoNewsletter} setBeehiivPreview={setBeehiivPreview}
            sendingCampaign={sendingCampaign} publishingBeehiiv={publishingBeehiiv}
            authHeader={authHeader}
            nlFeatured={nlFeatured}
            loadingNlFeatured={loadingNlFeatured}
            loadNlFeatured={loadNlFeatured}
            removeNlFeatured={removeNlFeatured}
            clearNlFeatured={clearNlFeatured}
          />

          <SourcesTab
            dm={dm}
            sources={sourcesHealth}
            loadingSources={loadingSources}
            loadSources={() => { loadSources(); loadTopicWatchers(); loadCustomSources(); loadSemanticDedup(); }}
            ppEnabled={ppEnabled} setPpEnabled={setPpEnabled}
            ppTopics={ppTopics} setPpTopics={setPpTopics}
            ppLastRunAt={ppLastRunAt} ppLastRunCount={ppLastRunCount}
            savingPp={savingPp} savePpDiscovery={savePpDiscovery}
            topicWatchers={topicWatchers}
            savingWatcher={savingWatcher}
            updateWatcher={updateWatcher}
            runWatcherNow={runWatcherNow}
            deleteWatcher={deleteWatcher}
            createWatcher={createWatcher}
            customSources={customSources}
            prefillQuickAddUrl={sourcePrefillUrl}
            clearPrefill={() => setSourcePrefillUrl("")}
            verifyCustomSource={verifyCustomSource}
            verifyCustomSourceHtml={verifyCustomSourceHtml}
            createCustomSource={createCustomSource}
            deleteCustomSource={deleteCustomSource}
            updateCustomSource={updateCustomSource}
            semanticDedup={semanticDedup}
            savingSemanticDedup={savingSemanticDedup}
            saveSemanticDedup={saveSemanticDedup}
          />

          <BriefTab
            dm={dm} brief={brief} briefContent={briefContent} setBriefContent={setBriefContent}
            briefPhotoUrl={briefPhotoUrl} setBriefPhotoUrl={setBriefPhotoUrl}
            briefPublished={briefPublished} setBriefPublished={setBriefPublished}
            generatingBrief={generatingBrief} savingBrief={savingBrief} loadingBrief={loadingBrief}
            generateBrief={generateBrief} saveBrief={saveBrief} saveDraft={saveDraft}
            uploadBriefPhoto={uploadBriefPhoto} photoInputRef={photoInputRef} uploadingPhoto={uploadingPhoto}
            editorComment={editorComment} setEditorComment={setEditorComment}
            editorUrls={editorUrls} setEditorUrls={setEditorUrls}
            newEditorUrl={newEditorUrl} setNewEditorUrl={setNewEditorUrl}
            editorNotesActive={editorNotesActive} addingEditorUrl={addingEditorUrl}
            savingEditorNotes={savingEditorNotes} loadingEditorNotes={loadingEditorNotes}
            saveEditorNotes={saveEditorNotes} clearEditorNotes={clearEditorNotes} addEditorUrl={addEditorUrl}
            autoSettings={autoSettings} setAutoSettings={setAutoSettings}
            savingAutoSettings={savingAutoSettings} saveAutoSettings={saveAutoSettings}
            briefOgPrompt={briefOgPrompt} setBriefOgPrompt={setBriefOgPrompt}
            regeneratingBriefOg={regeneratingBriefOg} regenerateBriefOg={regenerateBriefOg}
            socialTexts={socialTexts} setSocialTexts={setSocialTexts}
            briefFeatured={briefFeatured}
            loadingBriefFeatured={loadingBriefFeatured}
            removeBriefFeatured={removeBriefFeatured}
            clearBriefFeatured={clearBriefFeatured}
            authHeader={authHeader}
          />

          <TickerTab
            dm={dm} tickerItems={tickerItems} setTickerItems={setTickerItems}
            tickerEnabled={tickerEnabled} setTickerEnabled={setTickerEnabled}
            tickerPause={tickerPause} setTickerPause={setTickerPause}
            tickerAutopilot={tickerAutopilot} setTickerAutopilot={setTickerAutopilot}
            tickerAiCurated={tickerAiCurated} setTickerAiCurated={setTickerAiCurated}
            tickerLastAutopilotAt={tickerLastAutopilotAt}
            autopilotHistory={autopilotHistory}
            restoreFromHistory={restoreFromHistory}
            runningAutopilot={runningAutopilot} runAutopilotNow={runAutopilotNow}
            savingTicker={savingTicker} loadingTicker={loadingTicker}
            newTickerText={newTickerText} setNewTickerText={setNewTickerText}
            newTickerUrl={newTickerUrl} setNewTickerUrl={setNewTickerUrl}
            addTickerItem={addTickerItem} removeTickerItem={removeTickerItem} saveTicker={saveTicker}
          />

          <ApiKeysTab
            dm={dm} apiKeys={apiKeys} keyValues={keyValues} setKeyValues={setKeyValues}
            showKey={showKey} setShowKey={setShowKey}
            savingKeys={savingKeys} loadingKeys={loadingKeys}
            testResults={testResults} setTestResults={setTestResults}
            saveApiKeys={saveApiKeys} removeApiKey={removeApiKey} testApiKey={testApiKey}
          />

          <MonetizeTab
            dm={dm} adsEnabled={adsEnabled} setAdsEnabled={setAdsEnabled}
            adsPublisherId={adsPublisherId} setAdsPublisherId={setAdsPublisherId}
            adsSlotId={adsSlotId} setAdsSlotId={setAdsSlotId}
            savingAds={savingAds} saveAdsenseConfig={saveAdsenseConfig}
          />

          <BillingTab dm={dm} authHeader={authHeader} />
          <OfficinaTab dm={dm} authHeader={authHeader} />
          <ReportBriefsTab dm={dm} authHeader={authHeader} />
          <RejectedTab dm={dm} authHeader={authHeader} />
          <TabsContent value="events">
            <div className="w-full" data-testid="events-admin-tab">
              <TravelEventsCard dm={dm} authHeader={authHeader} />
            </div>
          </TabsContent>
          <SuggestionsTab dm={dm} authHeader={authHeader}
            onCountsChange={(c) => setNewSuggestionsCount(c.new || 0)}
            onAddAsSource={(url) => {
              setSourcePrefillUrl(url);
              loadSources(); loadTopicWatchers(); loadCustomSources(); loadSemanticDedup();
              selectTab("sources");
            }} />

          <EmailTab
            dm={dm} brevoStats={brevoStats} loadingBrevo={loadingBrevo}
            syncingAll={syncingAll} loadBrevoStats={loadBrevoStats} syncAllToBrevo={syncAllToBrevo}
            resendKey={resendKey} setResendKey={setResendKey}
            showResendKey={showResendKey} setShowResendKey={setShowResendKey}
            fromEmail={fromEmail} setFromEmail={setFromEmail}
            fromName={fromName} setFromName={setFromName}
            siteUrl={siteUrl} setSiteUrl={setSiteUrl}
            saving={saving} saveEmailConfig={saveEmailConfig} testEmail={testEmail}
          />

          <SocialTab dm={dm} authHeader={authHeader} />

          <ExternalApiTab dm={dm} />
          <DocCatalogTab dm={dm} />
          <RankingHealthTab dm={dm} />

          <PodcastTab dm={dm} />

          <FullAutopilotTab dm={dm} />
        </Tabs>
      </main>

      <BeehiivPreviewModal
        dm={dm}
        beehiivPreview={beehiivPreview}
        setBeehiivPreview={setBeehiivPreview}
        publishingBeehiiv={publishingBeehiiv}
        publishToBeehiiv={publishToBeehiiv}
      />
    </div>
  );
}
